import { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, LayersControl, useMap } from 'react-leaflet';
import { axios } from '../App';
import { toast } from 'sonner';
import { Plane, Waves, Droplets, Zap, Anchor, Navigation, Map as MapIcon, Layers } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

// Visakhapatnam Naval AOR (Area of Responsibility)
const VIZAG = { lat: 17.6868, lng: 83.2185 };
// Vizag Port / Naval Harbour — engagement zone is centred over the actual harbour mouth, not the city
const VIZAG_PORT = { lat: 17.6850, lng: 83.3000 };

// Real-world tactical landmarks around the Vizag harbour
const VIZAG_LANDMARKS = [
  { name: 'Eastern Naval Command HQ',  lat: 17.7100, lng: 83.3050, kind: 'naval' },
  { name: 'Visakhapatnam Naval Dockyard', lat: 17.6939, lng: 83.2807, kind: 'naval' },
  { name: 'INS Virbahu (Submarine Base)', lat: 17.6889, lng: 83.2920, kind: 'naval' },
  { name: 'Visakhapatnam Port Trust', lat: 17.6900, lng: 83.2980, kind: 'port' },
  { name: 'Outer Harbour Entrance', lat: 17.6731, lng: 83.3100, kind: 'port' },
  { name: 'RK Beach',                 lat: 17.7140, lng: 83.3287, kind: 'coast' },
  { name: 'Visakhapatnam Airport',    lat: 17.7212, lng: 83.2245, kind: 'air' },
  { name: 'Dolphin Nose Lighthouse',  lat: 17.6796, lng: 83.2851, kind: 'coast' },
];

// Custom divIcons per domain (replaces the default blue Leaflet pin)
const makeIcon = (color, glyph) =>
  L.divIcon({
    className: '',
    html: `<div style="
      width:34px;height:34px;border-radius:50%;
      background:${color}22;border:2px solid ${color};
      box-shadow:0 0 12px ${color}99,inset 0 0 6px ${color}66;
      display:flex;align-items:center;justify-content:center;
      color:${color};font-family:ui-monospace,monospace;font-weight:700;font-size:14px;
    ">${glyph}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });

const ICONS = {
  air:        makeIcon('#0ea5e9', '▲'),
  surface:    makeIcon('#10b981', '■'),
  underwater: makeIcon('#3b82f6', '●'),
  unknown:    makeIcon('#a855f7', '◆'),
  landmark_naval: makeIcon('#f59e0b', '⚓'),
  landmark_port:  makeIcon('#fbbf24', '⚓'),
  landmark_coast: makeIcon('#fde047', '◐'),
  landmark_air:   makeIcon('#fde047', '✈'),
};

function CenterOnVizagButton() {
  const map = useMap();
  return (
    <button
      onClick={() => map.flyTo([VIZAG.lat, VIZAG.lng], 12, { duration: 1.2 })}
      data-testid="map-recenter-vizag"
      className="absolute top-3 left-14 z-[400] bg-zinc-950/90 hover:bg-zinc-900 backdrop-blur border border-zinc-700 hover:border-sky-500 text-zinc-300 hover:text-sky-300 px-2.5 py-1.5 rounded-sm text-[10px] font-mono uppercase tracking-wider flex items-center gap-1.5 shadow-lg"
      title="Recenter on Visakhapatnam"
    >
      <Navigation size={12} /> Vizag
    </button>
  );
}

export default function DroneTracking() {
  const [fusedDetections, setFusedDetections] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [fusedRes, sensorsRes] = await Promise.all([
        axios.get('/fusion/detections'),
        axios.get('/sensors')
      ]);
      setFusedDetections(fusedRes.data);
      setSensors(sensorsRes.data);
    } catch (error) {
      toast.error('Failed to load tracking data');
    } finally {
      setLoading(false);
    }
  };

  const handleFusionAnalysis = async () => {
    setAnalyzing(true);
    try {
      const { data } = await axios.post('/fusion/analyze');
      toast.success(`AI Fusion Complete: ${data.fused_count} detections`);
      loadData();
    } catch (error) {
      toast.error('Fusion analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const filteredDetections = useMemo(
    () => (selectedDomain === 'all' ? fusedDetections : fusedDetections.filter(d => d.domain === selectedDomain)),
    [selectedDomain, fusedDetections]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sky-500 font-mono text-sm">LOADING TRACKING DATA...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="drone-tracking">
      <div className="bg-zinc-900/50 border-b border-zinc-800 p-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight uppercase">AI-Fused Drone Tracking</h1>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
            Multi-sensor fusion · AOR: <span className="text-sky-400">VISAKHAPATNAM</span> · 17.6868°N, 83.2185°E · Eastern Naval Command
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedDomain} onValueChange={setSelectedDomain}>
            <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800 rounded-none" data-testid="domain-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">All Domains</SelectItem>
              <SelectItem value="air">Air</SelectItem>
              <SelectItem value="surface">Surface</SelectItem>
              <SelectItem value="underwater">Underwater</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={handleFusionAnalysis}
            disabled={analyzing}
            className="bg-emerald-600 hover:bg-emerald-500 text-white border border-emerald-400/20 rounded-none uppercase text-xs"
            data-testid="run-fusion-button"
          >
            <Zap size={16} className={`mr-2 ${analyzing ? 'animate-pulse' : ''}`} />
            {analyzing ? 'Fusing...' : 'Run AI Fusion'}
          </Button>
        </div>
      </div>

      <div className="flex-1 relative">
        <MapContainer
          center={[VIZAG.lat, VIZAG.lng]}
          zoom={12}
          minZoom={6}
          maxZoom={18}
          style={{ height: '100%', width: '100%' }}
          className="z-0"
          worldCopyJump={true}
        >
          <LayersControl position="topright" collapsed={false}>
            {/* Realistic satellite imagery — Esri World Imagery */}
            <LayersControl.BaseLayer checked name="Satellite (Realistic)">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution='Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, GIS User Community'
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Streets (OpenStreetMap)">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap contributors'
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Tactical (Dark)">
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; OpenStreetMap, &copy; CARTO'
                maxZoom={19}
              />
            </LayersControl.BaseLayer>

            {/* Place-name labels overlay so satellite still shows Visakhapatnam, Madhurawada, etc. */}
            <LayersControl.Overlay checked name="Place Labels">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                attribution='Labels &copy; Esri'
                maxZoom={19}
              />
            </LayersControl.Overlay>
          </LayersControl>

          <CenterOnVizagButton />

          {/* AOR boundary — 25 km radius around Vizag harbour */}
          <Circle
            center={[VIZAG.lat, VIZAG.lng]}
            radius={25000}
            pathOptions={{ color: '#f59e0b', weight: 1.5, dashArray: '6 6', fillOpacity: 0.04 }}
          >
            <Popup>
              <div className="font-mono text-xs">
                <div className="font-bold text-amber-500 uppercase">AOR Boundary</div>
                <div>Visakhapatnam · 25 km radius</div>
              </div>
            </Popup>
          </Circle>

          {/* Inner harbour engagement zone — 5 km, centred over Vizag Port / Naval Harbour */}
          <Circle
            center={[VIZAG_PORT.lat, VIZAG_PORT.lng]}
            radius={5000}
            pathOptions={{ color: '#ef4444', weight: 1.5, dashArray: '4 4', fillColor: '#ef4444', fillOpacity: 0.05 }}
          >
            <Popup>
              <div className="font-mono text-xs">
                <div className="font-bold text-red-500 uppercase">Engagement Zone</div>
                <div>Vizag Port / Naval Harbour</div>
                <div>5 km radius · Close-in defence</div>
              </div>
            </Popup>
          </Circle>

          {/* Tactical landmarks */}
          {VIZAG_LANDMARKS.map((lm, i) => (
            <Marker key={`lm-${i}`} position={[lm.lat, lm.lng]} icon={ICONS[`landmark_${lm.kind}`] || ICONS.landmark_naval}>
              <Popup>
                <div className="font-mono text-xs">
                  <div className="font-bold text-amber-500 uppercase">{lm.name}</div>
                  <div className="text-zinc-400 capitalize">{lm.kind}</div>
                  <div className="text-zinc-500">{lm.lat.toFixed(4)}°N, {lm.lng.toFixed(4)}°E</div>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Sensor coverage rings */}
          {sensors.map((sensor) => (
            <Circle
              key={sensor.id}
              center={[sensor.lat, sensor.lng]}
              radius={(sensor.range_km || 5) * 1000}
              pathOptions={{ color: '#0ea5e9', fillColor: '#0ea5e9', fillOpacity: 0.08, weight: 1 }}
            >
              <Popup>
                <div className="font-mono text-xs">
                  <div className="font-bold text-sky-500 uppercase">{sensor.name}</div>
                  <div className="text-zinc-400">Type: {sensor.sensor_type}</div>
                  <div className="text-zinc-400">Domain: {sensor.domain}</div>
                  <div className="text-zinc-400">Range: {sensor.range_km} km</div>
                </div>
              </Popup>
            </Circle>
          ))}

          {/* Fused threat detections */}
          {filteredDetections.map((detection) => (
            <Marker
              key={detection.id}
              position={[detection.lat, detection.lng]}
              icon={ICONS[detection.domain] || ICONS.unknown}
            >
              <Popup>
                <div className="font-mono text-xs max-w-xs">
                  <div className="font-bold text-emerald-500 uppercase mb-2">AI-FUSED DETECTION</div>
                  <div className="space-y-1">
                    <div className="text-zinc-300">ID: {detection.drone_id}</div>
                    <div className="text-zinc-400">Domain: {detection.domain}</div>
                    {detection.drone_type && <div className="text-zinc-400">Type: {detection.drone_type}</div>}
                    <div className="text-zinc-400">Threat: {detection.threat_level}</div>
                    <div className="text-emerald-400">Fusion Confidence: {(detection.fusion_confidence * 100).toFixed(0)}%</div>
                    <div className="text-sky-400">Sensors: {detection.sensor_count} ({(detection.contributing_sensors || []).join(', ')})</div>
                    {detection.speed && <div className="text-zinc-400">Speed: {detection.speed} km/h</div>}
                    {detection.altitude && <div className="text-zinc-400">Altitude: {detection.altitude} m</div>}
                    {detection.ai_analysis && (
                      <div className="mt-2 p-2 bg-zinc-900 rounded text-zinc-300 text-xs">
                        <div className="font-bold text-emerald-500 mb-1">AI Analysis:</div>
                        {detection.ai_analysis}
                      </div>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Stats overlay */}
        <div className="absolute top-4 right-4 z-[400] bg-black/85 backdrop-blur-md border border-zinc-700 p-4 rounded-sm shadow-2xl" data-testid="map-stats">
          <h3 className="text-[11px] font-bold uppercase tracking-wider mb-3 text-emerald-400 flex items-center gap-1.5">
            <Zap size={12} /> AI-Fused Detections
          </h3>
          <div className="space-y-2 text-xs font-mono">
            <div className="flex items-center justify-between gap-6">
              <span className="flex items-center gap-2"><Plane className="text-sky-400" size={13} /><span className="text-zinc-300">Air</span></span>
              <span className="text-sky-400 font-bold">{fusedDetections.filter(d => d.domain === 'air').length}</span>
            </div>
            <div className="flex items-center justify-between gap-6">
              <span className="flex items-center gap-2"><Waves className="text-emerald-400" size={13} /><span className="text-zinc-300">Surface</span></span>
              <span className="text-emerald-400 font-bold">{fusedDetections.filter(d => d.domain === 'surface').length}</span>
            </div>
            <div className="flex items-center justify-between gap-6">
              <span className="flex items-center gap-2"><Droplets className="text-blue-400" size={13} /><span className="text-zinc-300">Underwater</span></span>
              <span className="text-blue-400 font-bold">{fusedDetections.filter(d => d.domain === 'underwater').length}</span>
            </div>
            <div className="border-t border-zinc-700 mt-2 pt-2 flex items-center justify-between">
              <span className="text-zinc-400">Total</span>
              <span className="text-emerald-400 font-bold">{fusedDetections.length}</span>
            </div>
          </div>
        </div>

        {/* Legend (bottom-left) */}
        <div className="absolute bottom-6 left-3 z-[400] bg-black/85 backdrop-blur-md border border-zinc-700 px-3 py-2.5 rounded-sm shadow-2xl text-[10px] font-mono uppercase" data-testid="map-legend">
          <div className="text-zinc-500 tracking-widest mb-1.5 flex items-center gap-1"><Layers size={11} /> Legend</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <span className="flex items-center gap-1.5"><span className="text-sky-400">▲</span><span className="text-zinc-300">Air</span></span>
            <span className="flex items-center gap-1.5"><span className="text-emerald-400">■</span><span className="text-zinc-300">Surface</span></span>
            <span className="flex items-center gap-1.5"><span className="text-blue-400">●</span><span className="text-zinc-300">Underwater</span></span>
            <span className="flex items-center gap-1.5"><span className="text-amber-400"><Anchor size={10} className="inline" /></span><span className="text-zinc-300">Naval / Port</span></span>
            <span className="flex items-center gap-1.5"><span className="text-amber-500">- - -</span><span className="text-zinc-300">25km AOR</span></span>
            <span className="flex items-center gap-1.5"><span className="text-red-500">- - -</span><span className="text-zinc-300">5km Engage</span></span>
          </div>
        </div>
      </div>
    </div>
  );
}
